import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import {
  DollarSign, TrendingUp, TrendingDown, Calendar, Download,
  Eye, ArrowUpRight, ArrowDownRight, Wallet, Percent,
  PieChart as PieChartIcon, BarChart3, Award, Crown,
  Sparkles, Zap, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Revenue = () => {
  const [dateRange, setDateRange] = useState('month');
  const [chartType, setChartType] = useState('revenue');

  // Revenue Data
  const revenueData = [
    { month: 'Jan', revenue: 325000, commission: 48750, partnerShare: 276250, orders: 245 },
    { month: 'Feb', revenue: 412000, commission: 61800, partnerShare: 350200, orders: 278 },
    { month: 'Mar', revenue: 398000, commission: 59700, partnerShare: 338300, orders: 312 },
    { month: 'Apr', revenue: 456000, commission: 68400, partnerShare: 387600, orders: 345 },
    { month: 'May', revenue: 523000, commission: 78450, partnerShare: 444550, orders: 389 },
    { month: 'Jun', revenue: 498000, commission: 74700, partnerShare: 423300, orders: 367 },
    { month: 'Jul', revenue: 567000, commission: 85050, partnerShare: 481950, orders: 412 },
    { month: 'Aug', revenue: 589000, commission: 88350, partnerShare: 500650, orders: 425 },
    { month: 'Sep', revenue: 578000, commission: 86700, partnerShare: 491300, orders: 418 },
    { month: 'Oct', revenue: 612000, commission: 91800, partnerShare: 520200, orders: 435 },
    { month: 'Nov', revenue: 645000, commission: 96750, partnerShare: 548250, orders: 448 },
    { month: 'Dec', revenue: 720000, commission: 108000, partnerShare: 612000, orders: 465 },
  ];

  // Commission Breakdown
  const commissionData = [
    { name: 'Platform Commission', value: 15, color: '#3b82f6' },
    { name: 'Partner Share', value: 85, color: '#10b981' },
  ];

  // Category Revenue
  const categoryRevenue = [
    { name: 'Indoor Plants', revenue: 1250000, percentage: 35, color: '#10b981' },
    { name: 'Outdoor Plants', revenue: 890000, percentage: 25, color: '#3b82f6' },
    { name: 'Flowering Plants', revenue: 720000, percentage: 20, color: '#f59e0b' },
    { name: 'Air Purifying', revenue: 430000, percentage: 12, color: '#8b5cf6' },
    { name: 'Others', revenue: 285000, percentage: 8, color: '#ef4444' },
  ];

  // Recent Transactions
  const recentTransactions = [
    { id: 'TXN-001', orderId: 'ORD-7829', seller: 'Green Thumb Nursery', amount: 2450, commission: 367, partnerShare: 2083, date: 'Oct 24, 2024', status: 'Settled' },
    { id: 'TXN-002', orderId: 'ORD-7830', seller: 'City Gardens', amount: 1200, commission: 180, partnerShare: 1020, date: 'Oct 25, 2024', status: 'Settled' },
    { id: 'TXN-003', orderId: 'ORD-7831', seller: 'Flora Bliss', amount: 4890, commission: 733, partnerShare: 4157, date: 'Oct 25, 2024', status: 'Pending' },
    { id: 'TXN-004', orderId: 'ORD-7832', seller: 'Herbals Co.', amount: 850, commission: 127, partnerShare: 723, date: 'Oct 26, 2024', status: 'Processing' },
  ];

  const stats = {
    totalRevenue: 612000,
    totalCommission: 91800,
    partnerShare: 520200,
    monthlyGrowth: 17,
    commissionRate: 15,
    pendingSettlements: 4890,
    avgOrderValue: 1407
  };

  const StatCard = ({ title, value, change, icon: Icon, color, bgColor, prefix = '', suffix = '' }) => (
    <div className="stat-card">
      <div className="stat-header">
        <span className="stat-title">{title}</span>
        <div className="stat-icon" style={{ backgroundColor: bgColor }}>
          <Icon size={18} color={color} />
        </div>
      </div>
      <div className="stat-value">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</div>
      <div className="stat-footer">
        <span className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
          {change >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {Math.abs(change)}%
        </span>
        <span className="stat-period">vs last month</span>
      </div>
    </div>
  );

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Settled': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Pending': return { icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'Processing': return { icon: AlertCircle, color: '#3b82f6', bg: '#dbeafe' };
      default: return { icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  return (
    <div className="partner-revenue-modern">
      {/* Header */}
      <div className="revenue-header">
        <div>
          <h1 className="page-title">Revenue Insights</h1>
          <p className="page-subtitle">Track earnings, commissions, and financial performance</p>
        </div>
        <div className="header-actions">
          <div className="date-range">
            <Calendar size={16} />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <button className="btn-export">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You have read-only access. You can view revenue data but cannot modify financial settings.</span>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard title="Total Revenue" value={stats.totalRevenue} change={17} icon={DollarSign} color="#10b981" bgColor="#d1fae5" prefix="₹" />
        <StatCard title="Platform Commission" value={stats.totalCommission} change={15.2} icon={Percent} color="#3b82f6" bgColor="#dbeafe" prefix="₹" />
        <StatCard title="Partner Share" value={stats.partnerShare} change={17.8} icon={Wallet} color="#8b5cf6" bgColor="#e9d5ff" prefix="₹" />
        <StatCard title="Avg Order Value" value={stats.avgOrderValue} change={4.7} icon={TrendingUp} color="#f59e0b" bgColor="#fed7aa" prefix="₹" />
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Revenue Trend Chart */}
        <div className="chart-card large">
          <div className="chart-header">
            <div>
              <h3>Revenue & Partner Share Trend</h3>
              <p>Monthly financial performance overview</p>
            </div>
            <div className="chart-badge">
              <TrendingUp size={14} />
              <span>+{stats.monthlyGrowth}% Growth</span>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorPartnerShare" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`, '']}
                />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" name="Total Revenue" />
                <Area type="monotone" dataKey="partnerShare" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorPartnerShare)" name="Partner Share" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Commission Split */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Commission Split</h3>
            <p>Platform vs Partner earnings</p>
          </div>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height="200px">
              <PieChart>
                <Pie
                  data={commissionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {commissionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="commission-list">
            {commissionData.map((item, idx) => (
              <div key={idx} className="commission-item">
                <span className="commission-color" style={{ backgroundColor: item.color }}></span>
                <span className="commission-name">{item.name}</span>
                <span className="commission-value">{item.value}%</span>
              </div>
            ))}
          </div>
          <div className="commission-note">
            <span>Current Commission Rate: {stats.commissionRate}%</span>
          </div>
        </div>
      </div>

      {/* Category Revenue */}
      <div className="chart-card">
        <div className="chart-header">
          <h3>Revenue by Category</h3>
          <p>Top performing product categories</p>
        </div>
        <div className="category-revenue-grid">
          {categoryRevenue.map((cat, idx) => (
            <div key={idx} className="category-revenue-item">
              <div className="category-header">
                <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                <span className="category-name">{cat.name}</span>
              </div>
              <div className="category-stats">
                <div className="category-amount">₹{cat.revenue.toLocaleString()}</div>
                <div className="category-percentage">{cat.percentage}%</div>
              </div>
              <div className="category-progress">
                <div className="progress-fill" style={{ width: `${cat.percentage}%`, backgroundColor: cat.color }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="data-card">
        <div className="card-header">
          <h3>Recent Transactions</h3>
          <Link to="/partner/transactions" className="view-link">View All →</Link>
        </div>
        <div className="table-container">
          <div className="table-responsive">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Order ID</th>
                  <th>Seller</th>
                  <th>Amount</th>
                  <th>Commission</th>
                  <th>Partner Share</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((tx) => {
                  const statusConfig = getStatusConfig(tx.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr key={tx.id}>
                      <td className="txn-id">{tx.id}</td>
                      <td className="order-id">{tx.orderId}</td>
                      <td className="seller-name">{tx.seller}</td>
                      <td className="amount">₹{tx.amount.toLocaleString()}</td>
                      <td className="commission">₹{tx.commission.toLocaleString()}</td>
                      <td className="partner-share">₹{tx.partnerShare.toLocaleString()}</td>
                      <td className="date">{tx.date}</td>
                      <td className="status">
                        <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                          <StatusIcon size={12} />
                          {tx.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Financial Insights */}
      <div className="insights-card">
        <div className="insights-header">
          <Zap size={20} />
          <h3>Financial Insights</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div>
              <h4>Revenue Growth</h4>
              <p>Revenue increased by 17% this month. Strong performance from Indoor Plants category.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">💰</div>
            <div>
              <h4>Partner Earnings</h4>
              <p>Your share this month is ₹5.20L. 85% of total platform revenue.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🎯</div>
            <div>
              <h4>Commission Optimization</h4>
              <p>Current commission rate is 15%. Consider volume-based discounts for top sellers.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">📊</div>
            <div>
              <h4>Pending Settlements</h4>
              <p>₹{stats.pendingSettlements.toLocaleString()} pending settlement from recent orders.</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .partner-revenue-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .revenue-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
        }

        .date-range select {
          border: none;
          background: transparent;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #92400e;
          font-size: 13px;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .stat-title {
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
        }

        .stat-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .stat-footer {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stat-change {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .stat-change.positive {
          color: #10b981;
        }

        .stat-change.negative {
          color: #ef4444;
        }

        .stat-period {
          font-size: 11px;
          color: #94a3b8;
        }

        /* Charts */
        .charts-row {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }

        .chart-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
        }

        .chart-card.large {
          margin-bottom: 0;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .chart-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .chart-header p {
          font-size: 12px;
          color: #94a3b8;
          margin: 2px 0 0;
        }

        .chart-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          background: #d1fae5;
          color: #10b981;
          border-radius: 20px;
          font-size: 12px;
        }

        .chart-container {
          height: 280px;
        }

        .pie-chart-container {
          display: flex;
          justify-content: center;
        }

        .commission-list {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin: 20px 0 12px;
        }

        .commission-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .commission-color {
          width: 12px;
          height: 12px;
          border-radius: 3px;
        }

        .commission-name {
          font-size: 13px;
          color: #475569;
        }

        .commission-value {
          font-weight: 600;
          color: #1e293b;
        }

        .commission-note {
          text-align: center;
          padding-top: 12px;
          border-top: 1px solid #e8ecef;
          font-size: 12px;
          color: #64748b;
        }

        /* Category Revenue */
        .category-revenue-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .category-revenue-item {
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .category-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .category-color {
          width: 10px;
          height: 10px;
          border-radius: 3px;
        }

        .category-name {
          font-size: 13px;
          font-weight: 500;
          color: #1e293b;
        }

        .category-stats {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .category-amount {
          font-weight: 600;
          color: #10b981;
          font-size: 14px;
        }

        .category-percentage {
          font-size: 12px;
          color: #64748b;
        }

        .category-progress {
          height: 6px;
          background: #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 10px;
        }

        /* Data Card */
        .data-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e8ecef;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .card-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .view-link {
          font-size: 13px;
          color: #10b981;
          text-decoration: none;
        }

        .view-link:hover {
          text-decoration: underline;
        }

        /* Transactions Table */
        .table-container {
          overflow-x: auto;
        }

        .transactions-table {
          width: 100%;
          border-collapse: collapse;
        }

        .transactions-table th {
          text-align: left;
          padding: 12px;
          background: #f8fafc;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          border-bottom: 1px solid #e8ecef;
        }

        .transactions-table td {
          padding: 12px;
          border-bottom: 1px solid #e8ecef;
          font-size: 13px;
        }

        .txn-id, .order-id {
          font-family: monospace;
          font-weight: 500;
          color: #10b981;
        }

        .amount, .commission, .partner-share {
          font-weight: 500;
        }

        .partner-share {
          color: #8b5cf6;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }

        /* Insights Card */
        .insights-card {
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          border-radius: 20px;
          padding: 24px;
        }

        .insights-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }

        .insights-header svg {
          color: #f59e0b;
        }

        .insights-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .insight-item {
          display: flex;
          gap: 14px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .insight-icon {
          font-size: 28px;
        }

        .insight-item h4 {
          font-size: 13px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px;
        }

        .insight-item p {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          line-height: 1.4;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .charts-row {
            grid-template-columns: 1fr;
          }
          .insights-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .partner-revenue-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .revenue-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .transactions-table {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default Revenue;